<template>
  <div>
    <h3>
      <translate>Select the period</translate>
      <b-form-select v-model="showTransactionsPeriod" :options="transactionsPeriods" class="w-auto">
      </b-form-select>
      <b-button size="sm" variant="primary" @click.prevent="generatePeriodReport" v-translate>
        Download CSV Report
      </b-button>
    </h3>
    <b-alert variant="success" show>
      <h2 class="my-0" v-translate="{totalAmountDisplay: this.totalAmountDisplay}">Selected period's benefits: %{totalAmountDisplay}</h2>
    </b-alert>
    <div v-if="transactions.length > 0">
      <b-table striped
        :items="transactions"
        :fields="transactionFields"
        responsive="lg"
        id="transactionsTable"
      >
      <template v-slot:table-colgroup="scope">
        <col
          v-for="field in scope.fields"
          :key="field.key"
          :style="{ width: field.key === 'addresses' ? '250px' : '' }"
        >
      </template>
        <template v-slot:cell(shipping_id)="data">
          <span v-if="data.value && data.item.shipping">
            <span v-if="data.item.reason === 'delivered' || data.item.shipping.company || (!data.item.shipping.company && data.item.shipping.courier)">
              <router-link :to="{name: 'ShippingsEdit', params: {'id': data.value}}" class="preview-link" target="_blank">
                #{{data.value | uuid }}
              </router-link>
            </span>
            <span v-else>#{{data.value | uuid }}</span>
          </span>
        </template>
        <template v-slot:cell(addresses)="data" class="bg-dark">
          <div class="shipping-address" style="width: 250px" v-translate="{from: data.item.from}" v-if="data.item.from">
            From: %{from}
          </div>
          <div class="shipping-address" style="width: 250px" v-translate="{to: data.item.to}" v-if="data.item.to">
            To: %{to}
          </div>
        </template>
        <template v-slot:cell(imputation_date)="data">
          {{moment.utc(data.value).local().format('L LT')}}
        </template>
        <template v-slot:cell(reason)="data">
          <b-badge pill :variant="data.value === 'delivered' ? 'success' : (data.value === 'incidence' || data.value === 'credit_note' || data.value === 'debit_note' ? 'warning' : 'danger')">
            <span v-show="data.value === 'delivered'"><translate>Delivered</translate></span>
            <span v-show="data.value === 'incidence'"><translate>Incidence</translate></span>
            <span v-show="data.value === 'returned'"><translate>Returned</translate></span>
            <span v-show="data.value === 'credit_note'"><translate>Credit Note</translate></span>
            <span v-show="data.value === 'debit_note'"><translate>Debit Note</translate></span>
          </b-badge>
          <small v-if="data.item.details">{{statusReasonsStrings[data.item.details.name] || data.item.details}} {{statusReasonEvents[data.item.details.event] || data.item.details.event}}</small>
        </template>
      </b-table>
      <b-pagination
        v-if="transactionsRows > transactionsPerPage"
        v-model="transactionsCurrentPage"
        :total-rows="transactionsRows"
        :per-page="transactionsPerPage"
        aria-controls="transactionsTable"
        align="center"
      ></b-pagination>
    </div>
    <b-alert variant="info" show v-else>
      <h3 class="my-0" v-translate>There were no transactions in the selected period</h3>
    </b-alert>
    <div class="d-none">
      <translate>pickup</translate>
      <translate>dropoff</translate>
    </div>
  </div>
</template>

<script>
import constants from '@/common/constants'

export default {
  name: 'TransactionsHistory',
  data() {
    return {
      transactions: [],
      transactionsPeriods: [],
      showTransactionsPeriod: null,
      totalAmountDisplay: '',
      intervalTransactions: null,
      intervalTransactionsLength: 60000,
      transactionsCurrentPage: 1,
      transactionsRows: null,
      transactionsPerPage: 25,
      periodReportParams: null
    }
  },
  computed: {
    transactionFields() {
      return [
        {
          key: 'shipping_id',
          label: this.$gettext('Shipping ID')
        },
        {
          key: 'addresses',
          label: this.$gettext('Addresses')
        },
        {
          key: 'courier',
          label: this.$gettext('Courier')
        },
        {
          key: 'amount_display',
          label: this.$gettext('Amount')
        },
        {
          key: 'imputation_date',
          label: this.$gettext('Date')
        },
        {
          key: 'reason',
          label: this.$gettext('Reason')
        }
      ]
    }
  },
  watch: {
    showTransactionsPeriod(val) {
      this.transactionsCurrentPage = 1
      this.transactionsRows = null
      this.fetchTransactions(val)
    },
    transactionsCurrentPage(val) {
      this.fetchTransactions()
    }
  },
  mixins: [constants],
  mounted() {
    if (this.profile.company_id) {
      this.companyCountryConfig = window.countries.find(x => x.iso_code === this.profile.company.country_iso_code).front_config
      this.generatePeriods()
    }
  },
  beforeDestroy() {
    clearTimeout(this.intervalTransactions)
  },
  methods: {
    setTimeoutTransactions() {
      clearTimeout(this.intervalTransactions)
      this.intervalTransactions = setTimeout(function () {
        this.generatePeriods()
        this.fetchTransactions()
      }.bind(this), this.intervalTransactionsLength)
    },
    clearTimeoutTransactions() {
      clearTimeout(this.intervalTransactions)
    },
    fetchTransactions(period = null) {
      let params = {
        page: this.transactionsCurrentPage - 1,
        limit: this.transactionsPerPage,
        sort: [
          {
            key: 'imputation_date',
            direction: 'desc'
          }
        ],
        date_from: period ? period.from : this.showTransactionsPeriod.from,
        date_to: period ? period.to : this.showTransactionsPeriod.to,
        includes: [
          'courier',
          'shipping'
        ],
        filter_groups: [
          {
            filters: [
              ['relationship', 'eq', 'provider']
            ]
          }
        ]
      }

      this.periodReportParams = {...params}
      this.periodReportParams.includes = []

      this.$http.get(this.$appConfig.api.baseUrl + 'companies/' + this.profile.company_id + '/transactions', {params: params})
        .then(response => {
          this.totalAmountDisplay = ''
          if (response.body.data.length > 0) {
            this.transactionsRows = response.body.pagination.total
            this.transactions = response.body.data.map(x => {
              x.amount = x.type === 'credit' || parseFloat(x.amount) === 0 ? parseFloat(x.amount) : -(parseFloat(x.amount))
              return {
                shipping_id: x.shipping_id || null,
                reason: x.reason,
                details: x.meta ? (x.meta.reason || (x.meta.meta !== '' ? x.meta.meta : x.meta.details.reason)) : ((x.reason === 'credit_note' || x.reason === 'debit_note') && x.reference ? x.reference : null),
                courier: x.courier ? x.courier.name : '',
                amount: x.amount,
                amount_display: x.type === 'credit' ? x.amount_formatted : '-' + x.amount_formatted,
                imputation_date: x.imputation_date,
                from: x.shipping ? x.shipping.address_from.formatted_address : null,
                to: x.shipping ? x.shipping.address_to.formatted_address : null,
                shipping: x.shipping
              }
            })
          } else {
            this.transactions = []
          }

          let params = {
            date_from: period ? period.from : this.showTransactionsPeriod.from,
            date_to: period ? period.to : this.showTransactionsPeriod.to
          }
          this.$http.get(this.$appConfig.api.baseUrl + 'companies/' + this.profile.company_id + '/transactions/summary', {params: params})
            .then(response => {
              this.totalAmountDisplay = response.body.benefit_formatted
            })
          this.setTimeoutTransactions()
        })
    },
    generatePeriods() {
      this.transactionsPeriods = []
      let from
      let to
      let baseDate = this.moment()
      console.log(baseDate.date())

      for (let period = 0; period < 2; period++) {
        if (baseDate.date() >= 8 && baseDate.date() < 23) {
          from = baseDate.clone().date(8)
          to = baseDate.clone().date(22)
        } else {
          from = baseDate.date() < 8 ? baseDate.clone().subtract(1, 'month').date(23) : baseDate.clone().date(23)
          to = baseDate.date() < 8 ? baseDate.clone().date(7) : baseDate.clone().add(1, 'month').date(7)
        }

        baseDate = from.clone().subtract(1, 'day')
        from = from.startOf('day').utc().format('YYYY-MM-DD HH:mm:ss')
        to = to.endOf('day').utc().format('YYYY-MM-DD HH:mm:ss')

        if (period === 0 && !this.showTransactionsPeriod) {
          this.showTransactionsPeriod = {from: from, to: to}
        }

        this.transactionsPeriods.push({value: {from: from, to: to}, text: this.moment.utc(from).local().format('L') + ' - ' + this.moment.utc(to).local().format('L') + (period === 0 ? ' ' + this.$gettext('(current)') : '')})
      }
    },
    generatePeriodReport() {
      this.periodReportParams.format = 'upload'
      this.$http.get(this.$appConfig.api.baseUrl + 'gcm/transactions', {params: this.periodReportParams})
        .then(response => {
          let obj = {
            title: this.$gettext('JOB DISPATCHED'),
            message: this.$gettext('The CSV Report is being generated, you will receive an email with a link to download it as soon as it\'s done'),
            type: 'success',
            customCloseBtnText: this.$gettext('Close')
          }
          this.$root.$children[0].$refs.simplert.openSimplert(obj)
        })
    }
  }
}
</script>
